import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, Clock, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSchedule } from "@/context/ScheduleContext";
import { formatWib } from "@/lib/wib";
import ProtectedImage from "@/components/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ScheduleStrip from "@/components/ScheduleStrip";
import ResponsiveFormDialog from "@/components/ResponsiveFormDialog";

export default function Checkout() {
  const { lines, setQty, clear, totalQty } = useCart();
  const { user } = useAuth();
  const sched = useSchedule();
  const navigate = useNavigate();
  const [purpose, setPurpose] = useState(sched.draft.purpose || "");
  const [purposeError, setPurposeError] = useState("");
  const [stock, setStock] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const [stockFailed, setStockFailed] = useState(false);
  const [stockLoaded, setStockLoaded] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [shared, setShared] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const errorBoxRef = useRef(null);

  const grouped = useMemo(() => {
    const g = {};
    lines.forEach((l) => { (g[l.category] = g[l.category] || []).push(l); });
    return g;
  }, [lines]);

  const dateSet = sched.ok;
  const equipment = sched.draft.booking_type === "WITH_EQUIPMENT";
  const legacyLines = lines.filter(l => l.is_bookable === false || l.bookable_for_new_booking === false || l.is_archived || l.item_code?.trim().toUpperCase() === "RGN-001" || l.category?.trim().toLowerCase() === "ruangan" || (stockLoaded && !Object.prototype.hasOwnProperty.call(stock, l.item_id)));


  const refetchStock = async () => {
    if (!equipment || !sched.ok) { setStock({}); setStockLoading(false); setStockFailed(false); setStockLoaded(false); return; }
    setStockLoading(true);
    setStockFailed(false);
    setStockLoaded(false);
    try {
      const { data } = await api.get("/items", {
        params: { start_time: sched.pickup, end_time: sched.ret },
      });
      const map = {};
      data.forEach((i) => { map[i.id] = i.available_qty; });
      setStock(map);
      setStockLoaded(true);
    } catch {
      setStockFailed(true);
    } finally {
      setStockLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(refetchStock, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sched.pickup, sched.ret, sched.ok, equipment]);

  const submit = async (e) => {
    e.preventDefault();
    if (!sched.ok) {
      const first = sched.errors.pickup || sched.errors.return || "Pilih jadwal dulu.";
      setSubmitError(first);
      toast.error(first);
      setTimeout(() => errorBoxRef.current?.focus(), 0);
      return;
    }
    if (!purpose.trim()) {
      setPurposeError("Isi acara peminjaman.");
      toast.error("Isi acara peminjaman.");
      return;
    }
    if (equipment && legacyLines.length) return toast.error("Keluarkan lokasi/alat legacy yang tidak dapat dipesan dari keranjang terlebih dahulu.");
    if (equipment && !lines.length) return toast.error("Keranjang kosong");
    setSaving(true);
    setSubmitError("");
    try {
      const { data } = await api.post("/bookings", {
        ...sched.payload,

        lines: equipment ? lines.map((l) => ({ item_id: l.item_id, qty: l.qty })) : [],
        purpose: purpose.trim(),
      });
      toast.success("Booking dibuat, menunggu approval admin");
      navigate(`/peminjaman/${data.id}`, { replace: true, state: { clearBookingDraft: true } });
    } catch (err) {
      // Validasi backend otoritatif: tampilkan pesan server apa adanya,
      // lalu refetch agar baris konflik tertandai akurat.
      const msg = errMsg(err);
      setSubmitError(msg);
      toast.error(msg);
      refetchStock();
      setTimeout(() => errorBoxRef.current?.focus(), 0);
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    try {
      await api.post("/templates", {
        name: templateName,
        lines: lines.map((l) => ({ item_id: l.item_id, qty: l.qty })),
        is_shared: shared,
      });
      toast.success("Template paket disimpan");
      setTemplateName("");
      setTemplateOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const durationLabel = () => {
    if (!sched.ok) return null;
    const ms = sched.endMs - sched.startMs;
    if (!isFinite(ms) || ms <= 0) return null;
    const mins = Math.round(ms / 60000);
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    return [d && `${d} hari`, h && `${h} jam`, m && `${m} mnt`].filter(Boolean).join(" ");
  };

  const dur = durationLabel();

  return (
    <div className="space-y-6 pb-24">
      <button data-testid="back-button" onClick={() => navigate(equipment ? "/katalog" : "/")}
        className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-[#004f9e] hover:underline">
        <ArrowLeft className="h-4 w-4" /> {equipment ? "Tambah alat lagi" : "Ubah persiapan acara"}
      </button>

      <div>
        <h1 className="font-heading text-[26px] font-semibold tracking-tight text-slate-900">Konfirmasi Booking</h1>
        <p className="mt-1.5 text-sm text-slate-500">Periksa jadwal bersama dan lokasi, lalu kirim untuk persetujuan admin.</p>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px]">
        <form onSubmit={submit} data-testid="checkout-form"
          className="h-fit space-y-5 rounded-lg border border-slate-200 bg-white p-5">
          <ScheduleStrip />
          {equipment && legacyLines.length > 0 && <div role="alert" className="rounded border border-amber-400 p-3 text-sm text-amber-800"><p>Keranjang lama berisi lokasi/alat yang tidak dapat dipesan: {legacyLines.map(l=>l.name).join(", ")}. Pilih lokasi pada Pilih Jadwal.</p><button type="button" className="mt-2 min-h-[44px] underline" onClick={() => legacyLines.forEach(l => setQty({id:l.item_id},0))}>Keluarkan {legacyLines.length} baris legacy dari keranjang</button></div>}
          <p className="text-sm text-slate-600">Lokasi: {sched.draft.location_source === "CUSTOM" ? sched.draft.custom_location : sched.draft.location_name || "Master lokasi terpilih"} · <button type="button" className="underline" onClick={() => navigate("/", { state: { returnTo: "/checkout" } })}>Ubah lokasi / jenis</button></p>

          {submitError && (
            <div ref={errorBoxRef} tabIndex={-1} role="alert"
              className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              <p><strong>Booking belum bisa dikirim.</strong></p>
              <p className="mt-1">{submitError}</p>
            </div>
          )}

          <div data-testid="return-date-preview" className="rounded-xl border border-slate-200 bg-slate-100 p-4 text-sm">
            {dur ? (
              <>
                <p className="flex items-center gap-2 font-semibold text-emerald-700">
                  <Clock className="h-4 w-4" /> Durasi booking: {dur}
                </p>
                <p className="mt-2 text-slate-800">Mulai: {formatWib(sched.startMs)}</p>
                <p className="text-slate-800">Selesai: {formatWib(sched.endMs)}</p>
                <p className="mt-2 text-xs text-slate-500">
                  {equipment ? "Mulai adalah waktu pengambilan; selesai adalah batas pengembalian. Kode mengikuti jadwal beserta buffer akses." : "Tanpa barang: tidak ada akses gudang atau pengembalian alat."}
                </p>
              </>
            ) : (
              <p className="text-slate-500">
                {stockLoading
                  ? "Mengecek ketersediaan stok…"
                  : "Pilih jadwal dulu untuk melihat ringkasan dan sisa stok."}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="checkout-event-input">Acara</Label>
            <Input data-testid="checkout-event-input" id="checkout-event-input" value={purpose}
              aria-invalid={!!purposeError}
              onChange={(e) => { setPurpose(e.target.value); sched.patch({ purpose: e.target.value }); setPurposeError(""); }}
              placeholder="Shooting konten, live event…"
              className="h-[44px] rounded-lg border-slate-300 bg-white text-[15px] text-slate-900" />
            <p className="text-xs text-slate-500">Nama acara terlihat oleh pengguna lain di kalender internal.</p>
            {purposeError && <p className="text-[13px] font-semibold text-rose-700">{purposeError}</p>}
          </div>

          <div>
            <div data-testid="checkout-name-input" className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-600">
              {user?.name || ""}
            </div>
            <p className="mt-1 text-xs text-slate-500">Booking dibuat atas nama ini.</p>
          </div>

          <div className="max-md:sticky max-md:-mx-5 max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] max-md:z-10 max-md:border-t max-md:border-slate-200 max-md:bg-white max-md:px-5 max-md:pb-3 max-md:pt-3">
            <Button data-testid="submit-order-button" type="submit" disabled={saving || (equipment && !lines.length)}
              className="h-14 w-full rounded-lg border border-[#0067c8] bg-[#0067c8] text-base font-semibold text-white shadow-lg hover:border-[#004f9e] hover:bg-[#004f9e]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : equipment ? `Kirim Booking · ${totalQty} pcs` : "Kirim booking acara"}
            </Button>
          </div>
        </form>

        {equipment ? <div data-testid="cart-summary" className="h-fit space-y-4 rounded-lg border border-slate-200 bg-white p-5 lg:sticky lg:top-20">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-[19px] font-semibold tracking-tight text-slate-900">List Peminjaman</h2>
            <div className="flex items-center gap-3">
              {lines.length > 0 && (
                <>
                  <button data-testid="save-template-button" onClick={() => setTemplateOpen(true)}
                    className="flex min-h-[44px] items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900">
                    <Bookmark className="h-4 w-4" /> Simpan paket
                  </button>
                  <ResponsiveFormDialog open={templateOpen} onOpenChange={setTemplateOpen}
                    tone="light" title="Simpan Template Paket"
                    description="Simpan kombinasi alat ini supaya sekali klik bisa dipakai lagi nanti.">
                    <div className="space-y-4">
                      <Input data-testid="template-name-input" value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Contoh: Multicam 3 Kamera"
                        className="h-[44px] rounded-lg border-slate-300 bg-white text-slate-900" />
                      <label className="flex items-center gap-3 text-sm text-slate-600">
                        <input data-testid="template-shared-checkbox" type="checkbox" checked={shared}
                          onChange={(e) => setShared(e.target.checked)} className="h-4 w-4 accent-[#0067c8]" />
                        Bagikan ke semua orang
                      </label>
                      <Button data-testid="confirm-save-template-button" onClick={saveTemplate}
                        className="h-[44px] w-full rounded-lg border border-[#0067c8] bg-[#0067c8] font-semibold text-white hover:border-[#004f9e] hover:bg-[#004f9e]">
                        Simpan
                      </Button>
                    </div>
                  </ResponsiveFormDialog>
                </>
              )}
              {lines.length > 0 && (
                <>
                  <button data-testid="clear-cart-button" onClick={() => setClearOpen(true)}
                    className="flex min-h-[44px] items-center gap-2 text-sm text-slate-500 hover:text-rose-700">
                    <Trash2 className="h-4 w-4" /> Kosongkan
                  </button>
                  <ResponsiveFormDialog open={clearOpen} onOpenChange={setClearOpen}
                    tone="light" title="Kosongkan keranjang?"
                    description={`${lines.length} jenis, ${totalQty} unit akan dihapus dari keranjang. Jadwal yang sudah dipilih tidak ikut terhapus.`}>
                    <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
                      <button data-testid="cancel-clear-cart-button" autoFocus
                        onClick={() => setClearOpen(false)}
                        className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-[18px] py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:border-slate-900">
                        Batal
                      </button>
                      <button data-testid="confirm-clear-cart-button"
                        onClick={() => { clear(); setClearOpen(false); toast.success("Keranjang dikosongkan"); }}
                        className="min-h-[44px] rounded-lg border border-red-200 bg-red-50 px-[18px] py-2.5 text-sm font-semibold text-red-900 transition-colors hover:bg-red-100">
                        Ya, kosongkan
                      </button>
                    </div>
                  </ResponsiveFormDialog>
                </>
              )}
            </div>
          </div>

          {lines.length === 0 && <p className="text-sm text-slate-500">Belum ada alat. Kembali ke katalog untuk menambah.</p>}

          {dateSet && stockLoading && (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Mengecek sisa stok…
            </p>
          )}
          {stockFailed && dateSet && (
            <div role="alert" className="rounded-xl border border-[#e8c95c] bg-[#fff8e6] p-3 text-[13px] text-[#6b4e0c]">
              <p><strong>Gagal mengecek sisa stok.</strong> Coba lagi untuk melihat ketersediaan pada jadwal ini.</p>
              <button onClick={refetchStock} className="mt-2 min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-900">Coba lagi</button>
            </div>
          )}

          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{cat}</p>
              {list.map((l) => {
                const sisa = stock[l.item_id];
                const kurang = dateSet && !stockFailed && sisa !== undefined && l.qty > sisa;
                return (
                  <div key={l.item_id} data-testid={`cart-line-${l.item_code}`}
                    className={`rounded-xl px-3 py-3 sm:px-4 ${kurang ? "border border-[#e8c95c] bg-[#fff8e6]" : "bg-slate-100"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <ProtectedImage src={l.photo} alt={l.name} compact
                        className="block h-[54px] w-[72px] shrink-0 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{l.name}</p>
                        <p data-testid={`cart-stock-${l.item_code}`} className="font-mono text-xs text-slate-500">
                          {l.item_code}
                          {dateSet && !stockFailed && sisa !== undefined && (
                            <span className={kurang ? " font-bold text-rose-700" : sisa <= 1 ? " font-bold text-amber-800" : " font-semibold text-emerald-700"}>
                              {" "}· sisa {sisa} pcs
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button data-testid={`cart-dec-${l.item_code}`} onClick={() => setQty({ ...l, id: l.item_id, quantity: l.max }, l.qty - 1)}
                          aria-label={`Kurangi ${l.name}`}
                          className="min-h-[44px] min-w-[44px] rounded-lg text-lg text-slate-900 hover:bg-slate-200">−</button>
                        <span className="min-w-10 text-center font-mono text-sm tabular-nums">{l.qty} pcs</span>
                        <button data-testid={`cart-inc-${l.item_code}`}
                          disabled={dateSet && !stockFailed && sisa !== undefined ? l.qty >= sisa : (l.max ? l.qty >= l.max : false)}
                          title={kurang ? undefined : dateSet && sisa !== undefined && l.qty >= sisa ? "Sudah mencapai sisa untuk jadwal ini" : undefined}
                          onClick={() => setQty({ ...l, id: l.item_id, quantity: l.max }, l.qty + 1)}
                          aria-label={`Tambah ${l.name}`}
                          className="min-h-[44px] min-w-[44px] rounded-lg text-lg text-slate-900 hover:bg-slate-200 disabled:opacity-30">+</button>
                      </div>
                    </div>
                    {kurang && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-[#6b4e0c]">
                        <span className="font-semibold">Bentrok: butuh {l.qty}, sisa {sisa}.</span>
                        <button onClick={() => setQty({ ...l, id: l.item_id, quantity: l.max }, sisa)}
                          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 font-semibold text-slate-900">
                          Kurangi ke {sisa}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div> : <aside className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600"><h2 className="mb-3 text-lg font-semibold text-slate-900">Tanpa barang</h2><p>Reservasi lokasi tetap memerlukan persetujuan. Acara diselesaikan otomatis setelah waktu selesai.</p></aside>}
      </div>
    </div>
  );
}
